<template>
  <ForwardsPageComponent>
    <LoadingIndicatorComponent
      v-if="store.loading && store.deal === null"
      :message="$t('forwards.deals.loading')"
    />

    <div v-else-if="store.notFound" class="card">
      <div class="card-body text-center py-5">
        <FontAwesomeIcon
          icon="fa-solid fa-lock"
          size="2x"
          class="text-body-tertiary mb-3"
        />
        <h5>{{ $t("forwards.deals.not_found_title") }}</h5>
        <p class="text-body-tertiary mb-3">
          {{ $t("forwards.deals.not_found_body") }}
        </p>
        <RouterLink
          :to="{ name: 'ForwardsOverview' }"
          class="btn btn-outline-primary btn-sm"
          >{{ $t("forwards.deals.header.back") }}</RouterLink
        >
      </div>
    </div>

    <div v-else-if="store.deal === null" class="card">
      <div class="card-body text-center py-5">
        <h5>{{ $t("forwards.deals.load_failed_title") }}</h5>
        <p class="text-body-tertiary mb-3">
          {{ $t("forwards.deals.load_failed_body") }}
        </p>
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          @click="reload"
        >
          {{ $t("forwards.common.retry") }}
        </button>
      </div>
    </div>

    <template v-else>
      <DealHeaderComponent :deal="store.deal" />

      <div
        v-if="store.deal.status === 'draft'"
        class="alert alert-warning d-flex align-items-center gap-2 mb-3"
        role="alert"
        data-testid="draft-deal-warning"
      >
        <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
        <span>{{ $t("forwards.deals.draft_warning") }}</span>
      </div>

      <p v-if="headerErrorMessage" class="error-note mb-3" role="alert">
        <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
        <span>{{ headerErrorMessage }}</span>
      </p>

      <p
        v-for="(warning, index) in commitmentWarnings"
        :key="index"
        class="advisory-note mb-3"
      >
        <FontAwesomeIcon icon="fa-regular fa-circle-info" />
        <span>{{ warning }}</span>
      </p>

      <div class="row g-3">
        <div class="col-lg-8 d-flex flex-column">
          <template v-if="store.isPayAsProduced">
            <PapScheduleCardComponent
              ref="papScheduleCard"
              v-model:editing="editingPapSchedule"
              :windowStart="store.deal.pap_window_start"
              :windowEnd="store.deal.pap_window_end"
              :tolerance="store.deal.pap_volume_tolerance"
              :months="papMonths"
              :deliveries="store.deal.deliveries"
              :planVersion="store.deal.plan_version"
              :pools="store.commitmentGroups"
              :canEdit="store.deal.capabilities.can_edit_pap_schedule"
              :canCorrectShipped="store.deal.capabilities.can_correct_shipped"
              :canCancel="store.deal.capabilities.can_cancel"
              :busy="store.busyAction === 'pap_schedule'"
              :busyAction="store.busyAction"
              :errorMessage="errorOn('pap-schedule')"
              @save="onSavePapSchedule"
              @cancel="openCancelDialog"
            />
            <PapMonthlyMonitoringComponent
              :months="papMonths"
              :deal="store.deal"
              :managesDeal
              :loading="store.papMonitoringLoading"
              @delivered="reload"
              @producer-prices="openProducerPricesModal"
            />
          </template>

          <template v-else>
            <DeliveryPlanEditorComponent
              v-if="showEditor"
              :deal="store.deal"
              :preview="store.planPreview"
              :busy="store.busyAction === 'plan'"
              :errorMessage="errorOn('plan-editor')"
              @dry-run="store.previewPlan"
              @discard="closeEditor"
              @save="onSavePlan"
            />
            <DeliveryPlanSectionComponent
              v-else
              :deal="store.deal"
              :offeringSheetUrls="store.offeringSheetUrls"
              :busyAction="store.busyAction"
              :highlightedDeliveryId="highlightedDeliveryId"
              :scrollToHighlighted="
                highlightedDeliveryId !== scrolledDeliveryId
              "
              @edit-plan="showEditor = true"
              @generate-offering-sheet="store.generateOfferingSheet"
              @slice-action="onSliceAction"
              @delivered="reload"
              @producer-prices="openProducerPricesModal"
              @delivery-notes="openDeliveryNotesModal"
              @confirm="store.confirm"
              @unconfirm="openUnconfirmDialog"
              @cancel="openCancelDialog"
              @scrolled="scrolledDeliveryId = $event"
            />
          </template>

          <FreeEscrowCardComponent
            v-if="store.freeEscrow.length > 0"
            :rows="store.freeEscrow"
            :canReturn="managesDeal"
            @return-to-owner="openEscrowModal(null)"
          />
        </div>

        <div class="col-lg-4">
          <DealMetadataCardComponent
            v-model:editingCriteria="editingCriteria"
            :deal="store.deal"
            :busyAction="store.busyAction"
            :criteriaPreview
            @edit-metadata="openDialog({ kind: 'metadata' })"
            @save-criteria="onSaveCriteria"
          />
        </div>
      </div>

      <CommitmentsSectionComponent
        v-if="!store.isPayAsProduced"
        :groups="store.commitmentGroups"
        @show-escrow="openEscrowModal"
      />

      <DealMetadataEditModal
        v-model="metadataOpen"
        :deal="store.deal"
        :busy="store.busyAction === 'metadata'"
        :errorMessage="errorOn('metadata')"
        @submit="onSaveMetadata"
      />

      <AddCommitmentModal
        v-model="backOpen"
        :target="backTarget"
        :deliveries="store.deliveries"
        :pools="store.commitmentPools"
        :poolsLoading="store.commitmentPoolsLoading"
        :openVolumeMwh="store.openMwh"
        :busy="store.busyAction === 'add_commitment'"
        :errorMessage="errorOn('back')"
        @search="store.loadCommitmentPools"
        @submit="onAddCommitment"
      />

      <ResizeSliceModal
        v-model="resizeOpen"
        :slice="activeSlice"
        :busy="resizeBusy"
        :errorMessage="errorOn('resize')"
        :pool="store.resizePool"
        @submit="onResize"
      />

      <MoveSliceModal
        v-model="moveOpen"
        :slice="activeSlice"
        :deliveries="store.deliveries"
        :sourceDeliveryId="activeSliceDeliveryId"
        :busy="store.busyAction === 'move'"
        :errorMessage="errorOn('move')"
        @submit="onMove"
      />

      <NarrowSliceModal
        v-model="narrowOpen"
        :slice="activeSlice"
        :busy="store.busyAction === 'narrow'"
        :errorMessage="errorOn('narrow')"
        @submit="onNarrow"
      />

      <ProducerPricesModal
        ref="producerPricesModal"
        v-model="producerPricesOpen"
        :delivery="pricedDelivery"
        :dealId="store.deal.id"
        :planVersion="store.deal.plan_version"
        :readOnly="!managesDeal || pricedDeliveryFrozen"
        :busy="store.busyAction === 'producer_prices'"
        :errorMessage="errorOn('producer-prices')"
        @submit="onSaveProducerPrices"
      />

      <DeliveryNotesModal
        v-model="deliveryNotesOpen"
        :delivery="notedDelivery"
        :busy="store.busyAction === 'delivery_notes'"
        :errorMessage="errorOn('delivery-notes')"
        @submit="onSaveDeliveryNotes"
      />

      <ReturnToOwnerModal
        v-model="escrowReturnOpen"
        :rows="store.escrowAttributions"
        :commitmentId="escrowCommitmentId"
        :freeAttributionIds="freeAttributionIds"
        :title="escrowModalTitle"
        :contributions="escrowContributions"
        :canReturn="escrowCanReturn"
        :commitmentGone="escrowCommitmentGone"
        :loading="store.escrowAttributionsLoading"
        :busy="store.busyAction === 'escrow_return'"
        :errorMessage="errorOn('escrow-return')"
        :results="escrowReturnDialog?.results ?? null"
        @submit="onReturnEscrow"
      />

      <PullToEscrowModal
        v-model="escrowPullOpen"
        :commitmentId="pullCommitmentId"
        :members="pullMembers"
        :escrowedMwh="pullEscrowedMwh"
        :committedMwh="pullCommittedMwh"
        :commitmentGone="pullCommitmentGone"
        :busy="store.busyAction === 'escrow_pull'"
        :errorMessage="errorOn('escrow-pull')"
        :results="escrowPullDialog?.results ?? null"
        @submit="onPullEscrow"
      />

      <ConfirmationModalComponent
        :visible="isOpen('remove-backing')"
        :title="$t('forwards.deals.remove_backing.title')"
        :message="removeBackingMessage"
        :confirmButtonText="$t('forwards.deals.remove_backing.confirm')"
        confirmButtonClass="btn-danger"
        preserveWhitespace
        :closeOnConfirm="false"
        :busy="store.busyAction === 'delete_backing'"
        :errorMessage="errorOn('remove-backing')"
        @confirm="onRemoveBacking"
        @cancel="closeDialog('remove-backing')"
      />

      <ConfirmationModalComponent
        :visible="isOpen('unconfirm')"
        :title="$t('forwards.deals.plan.unconfirm_title')"
        :message="$t('forwards.deals.plan.unconfirm_body')"
        :confirmButtonText="$t('forwards.deals.plan.unconfirm')"
        confirmButtonClass="btn-primary"
        :closeOnConfirm="false"
        :busy="store.busyAction === 'unconfirm'"
        :errorMessage="errorOn('unconfirm')"
        @confirm="onUnconfirmDeal"
        @cancel="closeDialog('unconfirm')"
      />

      <ConfirmationModalComponent
        :visible="isOpen('cancel')"
        :title="$t('forwards.deals.plan.cancel_title')"
        :message="
          $t(
            store.isPayAsProduced
              ? 'forwards.deals.plan.cancel_body_pap'
              : 'forwards.deals.plan.cancel_body'
          )
        "
        :confirmButtonText="$t('forwards.deals.plan.cancel_deal')"
        confirmButtonClass="btn-danger"
        :closeOnConfirm="false"
        :busy="store.busyAction === 'cancel'"
        :errorMessage="errorOn('cancel')"
        @confirm="onCancelDeal"
        @cancel="closeDialog('cancel')"
      />
    </template>

    <!-- A pool edited from one of this deal's pool links can change its feasibility and commitments. -->
    <PoolEditorModalHost v-if="poolEditorAvailable" @written="reload" />
  </ForwardsPageComponent>
</template>

<script setup lang="ts">
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import {
  formatDateTimeIntoDate,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import { useQueryManager } from "@/composables/useQueryManager";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { managesClient } from "@/composables/useAdminCheck";
import ForwardsPageComponent from "@/forwards2/components/ForwardsPageComponent.vue";
import { providePoolEditorAvailable } from "@/forwards2/components/poolEditorAvailability";
import PoolEditorModalHost from "@/forwards2/pools/PoolEditorModalHost.vue";
import AddCommitmentModal, {
  type BackSliceTarget,
} from "@/forwards2/deals/AddCommitmentModal.vue";
import CommitmentsSectionComponent from "@/forwards2/deals/CommitmentsSectionComponent.vue";
import DealHeaderComponent from "@/forwards2/deals/DealHeaderComponent.vue";
import DealMetadataCardComponent from "@/forwards2/deals/DealMetadataCardComponent.vue";
import DealMetadataEditModal from "@/forwards2/deals/DealMetadataEditModal.vue";
import DeliveryPlanEditorComponent from "@/forwards2/deals/DeliveryPlanEditorComponent.vue";
import DeliveryPlanSectionComponent from "@/forwards2/deals/DeliveryPlanSectionComponent.vue";
import FreeEscrowCardComponent from "@/forwards2/deals/FreeEscrowCardComponent.vue";
import MoveSliceModal from "@/forwards2/deals/MoveSliceModal.vue";
import NarrowSliceModal from "@/forwards2/deals/NarrowSliceModal.vue";
import PapMonthlyMonitoringComponent from "@/forwards2/deals/PapMonthlyMonitoringComponent.vue";
import PapScheduleCardComponent from "@/forwards2/deals/PapScheduleCardComponent.vue";
import ProducerPricesModal from "@/forwards2/deals/ProducerPricesModal.vue";
import DeliveryNotesModal from "@/forwards2/deals/DeliveryNotesModal.vue";
import PullToEscrowModal from "@/forwards2/deals/PullToEscrowModal.vue";
import ResizeSliceModal from "@/forwards2/deals/ResizeSliceModal.vue";
import ReturnToOwnerModal from "@/forwards2/deals/ReturnToOwnerModal.vue";
import { isDeliveryFrozen } from "@/forwards2/deals/deliveryFrozen";
import type {
  SliceAction,
  SliceDialogKind,
} from "@/forwards2/deals/sliceActions";
import { type DealAction, useDealStore } from "@/forwards2/deals/useDealStore";
import { disposePageStore } from "@/forwards2/disposePageStore";
import type {
  BeyondStrategyRow,
  CriteriaPreview,
  DealMetadataRequest,
  DeliveryResponse,
  EligibilityCriteriaRequest,
  EscrowMoveItemResult,
  EscrowPullRequest,
  PapScheduleUpdateRequest,
  PlanStateRequest,
  ProducerPricesRequest,
  DeliveryNotesRequest,
  SliceResponse,
} from "@/forwards2/forwardsTypes";
import type { PeriodWidth } from "@/forwards2/periodUtils";
import {
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRoute } from "vue-router";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

/** The page's modal dialogs. Each entry carries only the ids its dialog reads the store by. */
type DealDialog =
  | { kind: SliceDialogKind; sliceId: number }
  | {
      kind: "escrow-return";
      commitmentId: number | null;
      results: EscrowMoveItemResult[] | null;
    }
  | {
      kind: "escrow-pull";
      commitmentId: number;
      results: EscrowMoveItemResult[] | null;
    }
  | { kind: "producer-prices"; deliveryId: number }
  | { kind: "delivery-notes"; deliveryId: number }
  | { kind: "metadata" }
  | { kind: "unconfirm" }
  | { kind: "cancel" };

type DealDialogKind = DealDialog["kind"];

type ErrorSurface = DealDialogKind | "plan-editor" | "pap-schedule" | "header";

// A 4xx sentence belongs to the surface that ran the action; the page header renders only what has
// no surface of its own. Resizing open volume is a plan save, so the resize dialog also reads `plan`.
const ERROR_SURFACES: Record<DealAction, readonly ErrorSurface[]> = {
  metadata: ["metadata"],
  criteria: ["header"],
  confirm: ["header"],
  unconfirm: ["unconfirm"],
  cancel: ["cancel"],
  offering_sheet: ["header"],
  plan: ["plan-editor", "resize"],
  move: ["move"],
  narrow: ["narrow"],
  resize: ["resize"],
  add_commitment: ["back"],
  delete_backing: ["remove-backing"],
  escrow_return: ["escrow-return"],
  escrow_pull: ["escrow-pull"],
  pap_schedule: ["pap-schedule"],
  producer_prices: ["producer-prices"],
  delivery_notes: ["delivery-notes"],
};

/**
 * One forward deal: its buyer and product definition, its delivery plan, and the commitments and
 * escrow behind it. A pay-as-produced deal has a generated plan instead, so it shows its nominated
 * schedule and monthly monitoring — the same page for every viewer, only the controls differ
 * (user decision, 2026-09-24).
 *
 * Every mutating call answers with the whole deal, so nothing here re-fetches after an action —
 * except the escrow moves, which answer with per-item results instead and are followed by a
 * reload. A deal the caller may not see is a 404, exactly like one that does not exist.
 */
const route = useRoute();
const store = useDealStore();
const accountStore = useAccountStore();
const { t } = useI18n();
const { getQueryValue, parsers } = useQueryManager();
const { globalErrorHandler } = useGlobalHandlers();

const deliveryParam = getQueryValue("delivery", parsers.string);

const showEditor = ref(false);
const editingPapSchedule = ref(false);
const papScheduleCard = useTemplateRef("papScheduleCard");
const producerPricesModal = useTemplateRef("producerPricesModal");
const editingCriteria = ref(false);
const criteriaPreview = shallowRef<CriteriaPreview | null>(null);
const commitmentWarnings = ref<string[]>([]);
// Closing a dialog only drops `open`: the entry stays until another dialog opens, so a closing modal
// keeps its content through the fade-out.
const activeDialog = shallowRef<(DealDialog & { open: boolean }) | null>(null);
// The delivery `?delivery=` was last scrolled to, kept here because the cards remount whenever the
// plan editor closes.
const scrolledDeliveryId = ref<number | null>(null);

const routeDealId = computed(() => {
  const raw = route.params.id;
  const value = Array.isArray(raw) ? raw[0] : raw;
  const parsed = Number(value);
  return value === undefined || Number.isNaN(parsed) ? null : parsed;
});

const highlightedDeliveryId = computed(() => {
  const parsed = Number(deliveryParam.value);
  return deliveryParam.value && !Number.isNaN(parsed) ? parsed : null;
});

const managesDeal = computed(
  () => store.deal?.capabilities.can_manage === true
);
// The deal reads platform-wide as the admin, but the pool editor reads as the session's account: a
// managed client sees only its own pools, so a link would open on a 404. The admin guard loaded the
// account on entering the page.
const poolEditorAvailable = computed(
  () => !managesClient(accountStore.account)
);
const freeAttributionIds = computed(() =>
  store.freeEscrow.map((row) => row.attribution_id)
);

const papMonths = computed(() => store.papMonitoring?.months ?? []);

const headerErrorMessage = computed(() => errorOn("header"));

const isOpen = (kind: DealDialogKind) =>
  activeDialog.value?.open === true && activeDialog.value.kind === kind;

/** Closes the dialog only if it is still the open one: an action may land after the user moved on. */
const closeDialog = (kind: DealDialogKind) => {
  if (isOpen(kind) && activeDialog.value !== null)
    activeDialog.value = { ...activeDialog.value, open: false };
};

/** A modal's `v-model`; the modals only ever write `false`, when dismissed. */
const dialogModel = (kind: DealDialogKind) =>
  computed({
    get: () => isOpen(kind),
    set: (open: boolean) => {
      if (!open) closeDialog(kind);
    },
  });

const metadataOpen = dialogModel("metadata");
const backOpen = dialogModel("back");
const resizeOpen = dialogModel("resize");
const moveOpen = dialogModel("move");
const narrowOpen = dialogModel("narrow");
const producerPricesOpen = dialogModel("producer-prices");
const deliveryNotesOpen = dialogModel("delivery-notes");
const escrowReturnOpen = dialogModel("escrow-return");
const escrowPullOpen = dialogModel("escrow-pull");

const activeSliceId = computed(() => {
  const dialog = activeDialog.value;
  return dialog !== null && "sliceId" in dialog ? dialog.sliceId : null;
});

const escrowReturnDialog = computed(() => {
  const dialog = activeDialog.value;
  return dialog?.kind === "escrow-return" ? dialog : null;
});

const escrowPullDialog = computed(() => {
  const dialog = activeDialog.value;
  return dialog?.kind === "escrow-pull" ? dialog : null;
});

const pricedDeliveryId = computed(() => {
  const dialog = activeDialog.value;
  return dialog?.kind === "producer-prices" ? dialog.deliveryId : null;
});

const activeSlice = computed<SliceResponse | null>(
  () => store.slices.find((slice) => slice.id === activeSliceId.value) ?? null
);

const activeSliceDeliveryId = computed(
  () =>
    store.deliveries.find((delivery) =>
      delivery.slices.some((slice) => slice.id === activeSliceId.value)
    )?.id ?? null
);

const pricedDelivery = computed<DeliveryResponse | null>(
  () =>
    store.deliveries.find(
      (delivery) => delivery.id === pricedDeliveryId.value
    ) ?? null
);

/** A shipped delivery's prices are what it invoiced at, and a voided one has no transaction left to price,
 *  so both are shown, never changed. */
const pricedDeliveryFrozen = computed(
  () =>
    pricedDelivery.value !== null &&
    isDeliveryFrozen(pricedDelivery.value.transaction_status)
);

const notedDelivery = computed<DeliveryResponse | null>(() => {
  const dialog = activeDialog.value;
  if (dialog?.kind !== "delivery-notes") return null;
  return (
    store.deliveries.find((delivery) => delivery.id === dialog.deliveryId) ??
    null
  );
});

/** The open row the Back menu entry was used on — the add-commitment modal backs exactly that one. */
const backTarget = computed<BackSliceTarget | null>(() => {
  const slice = activeSlice.value;
  const deliveryId = activeSliceDeliveryId.value;
  if (slice === null || deliveryId === null || !slice.is_open) return null;
  return {
    sliceId: slice.id,
    deliveryId,
    periodLabel: slice.period_label,
    volumeMwh: slice.volume_mwh,
  };
});

// Resizing open volume is a one-operation plan save, so it spins under the plan action, not resize.
const resizeBusy = computed(
  () => store.busyAction === "resize" || store.busyAction === "plan"
);

const commitmentById = (commitmentId: number | null) =>
  store.commitmentGroups
    .flatMap((group) => group.commitments)
    .find((commitment) => commitment.id === commitmentId) ?? null;

const groupOfCommitment = (commitmentId: number | null) =>
  store.commitmentGroups.find((group) =>
    group.commitments.some((commitment) => commitment.id === commitmentId)
  ) ?? null;

const escrowCommitmentId = computed(
  () => escrowReturnDialog.value?.commitmentId ?? null
);
const escrowCommitment = computed(() =>
  commitmentById(escrowCommitmentId.value)
);

/**
 * Whether a refresh took the commitment a dialog was opened for off the deal — its backing deleted,
 * the deal cancelled. The dialog says so and refuses its move, rather than falling back to the
 * free-escrow form it would otherwise look like. A failed load leaves no deal at all, which says
 * nothing about the commitment.
 */
const isCommitmentGone = (commitmentId: number | null) =>
  commitmentId !== null &&
  store.deal !== null &&
  commitmentById(commitmentId) === null;

const escrowCommitmentGone = computed(() =>
  isCommitmentGone(escrowCommitmentId.value)
);

/**
 * The escrow dialog: a commitment's per-company breakdown for everyone, the return form for a super
 * admin while the commitment is live. From the free-escrow card there is no commitment, only the
 * form. Read from the deal rather than copied on open, so a return's refresh reaches the open dialog.
 */
const escrowContributions = computed(
  () => escrowCommitment.value?.escrow_by_company ?? null
);
const escrowModalTitle = computed(() => {
  const commitment = escrowCommitment.value;
  const group = groupOfCommitment(escrowCommitmentId.value);
  return commitment && group
    ? t("forwards.deals.commitments.escrow_title", {
        pool: group.pool_name,
        date: formatDateTimeIntoDate(commitment.created_at),
      })
    : null;
});
const escrowCanReturn = computed(
  () =>
    managesDeal.value && (escrowCommitment.value?.released_at ?? null) === null
);

const pullCommitmentId = computed(
  () => escrowPullDialog.value?.commitmentId ?? null
);
const pullCommitment = computed(() => commitmentById(pullCommitmentId.value));
const pullCommitmentGone = computed(() =>
  isCommitmentGone(pullCommitmentId.value)
);
/** The owners the pull may draw from, carried on the deal under the viewer's own scope (C-09b-07). */
const pullMembers = computed(
  () => groupOfCommitment(pullCommitmentId.value)?.members ?? []
);

/**
 * What removing a backing costs, spelled out before it happens (user decision 2026-09-16): the
 * volume goes back on the market, and the pull's price snapshot dies with the commitment — wholly
 * when this was its last backed slice, in part otherwise.
 */
const removeBackingMessage = computed(() => {
  const slice = activeSlice.value;
  const commitment = commitmentById(slice?.commitment_id ?? null);
  if (slice === null || commitment === null) return "";

  const floor = commitment.minimum_price_snapshot
    ? t("forwards.deals.commitments.floor", {
        price: commitment.minimum_price_snapshot,
      })
    : t("forwards.deals.commitments.no_floor");
  const conditions =
    commitment.contango_condition_snapshot === "none"
      ? floor
      : `${floor} · ${t(`forwards.deals.commitments.contango.${commitment.contango_condition_snapshot}`)}`;

  return [
    t("forwards.deals.remove_backing.pull", {
      date: formatDateTimeIntoDate(commitment.created_at),
      conditions,
    }),
    t(
      commitment.slices.length > 1
        ? "forwards.deals.remove_backing.released_part"
        : "forwards.deals.remove_backing.released_whole",
      {
        mwh: formatNumberWithSpaces(slice.volume_mwh),
        pool: slice.pool_name ?? "",
      }
    ),
    t("forwards.deals.remove_backing.snapshot_lost"),
  ].join("\n\n");
});

const pullEscrowedMwh = computed(() =>
  (pullCommitment.value?.slices ?? []).reduce(
    (total, slice) => total + slice.escrowed_mwh,
    0
  )
);
const pullCommittedMwh = computed(() =>
  (pullCommitment.value?.slices ?? []).reduce(
    (total, slice) => total + slice.volume_mwh,
    0
  )
);

const reload = async () => {
  if (routeDealId.value !== null) await store.load(routeDealId.value);
  if (store.isPayAsProduced) await store.loadPapMonitoring();
};

watch(routeDealId, () => {
  // The schedule draft is keyed by month label, so another deal's months must not inherit it.
  // Closing the criteria editor also ends a pending criteria preview's session (see onSaveCriteria).
  editingPapSchedule.value = false;
  editingCriteria.value = false;
  void reload();
});

// A preview belongs to one editing session: reopened, the editor starts from the deal's criteria again.
watch(editingCriteria, (editing) => {
  if (!editing) criteriaPreview.value = null;
});

watch(
  () => store.loadError,
  (error) => {
    if (error !== null) globalErrorHandler(error);
  }
);

watch(
  () => store.actionError,
  (error) => {
    if (error !== null) globalErrorHandler(error);
  }
);

onMounted(() => void reload());

// The store is page-scoped: disposing it with its state is what lets the next deal open on the
// loading state rather than on this one.
onBeforeUnmount(() => disposePageStore(store));

const closeEditor = () => {
  showEditor.value = false;
  store.clearPlanPreview();
};

const onSavePlan = async (state: PlanStateRequest) => {
  if ((await store.savePlan(state)) !== null) closeEditor();
};

const onSaveMetadata = async (body: DealMetadataRequest) => {
  if ((await store.saveMetadata(body)) !== null) closeDialog("metadata");
};

const onAddCommitment = async (
  poolId: number,
  takes: Record<number, number>,
  ignoreSalesStrategy: boolean
) => {
  const result = await store.addCommitment(poolId, takes, ignoreSalesStrategy);
  if (result === null) return;
  // The commitment is written; a feasibility warning describes it rather than blocking it, so it
  // belongs on the page the modal returns to (RV-feasibility-06).
  commitmentWarnings.value = [
    ...result.warnings,
    ...soldBeyondStrategyNotes(result.beyond_sales_strategy),
  ];
  closeDialog("back");
};

/** What an override sold beyond the strategy, recalculated at the save, as the page's notes say it. */
const soldBeyondStrategyNotes = (row: BeyondStrategyRow | null): string[] =>
  row === null
    ? []
    : [
        t("forwards.deals.commitments.sold_beyond_strategy", {
          pool: row.pool_name,
          mwh: formatNumberWithSpaces(row.volume_mwh),
        }),
      ];

/**
 * The editor stays open until the save has actually landed, so a refusal keeps the entered numbers.
 * A stale save has had the deal reloaded by the store; once the card renders it, the card merges
 * its draft onto it.
 */
const onSavePapSchedule = async (body: PapScheduleUpdateRequest) => {
  const outcome = await store.savePapSchedule(body);
  if (outcome === "saved") editingPapSchedule.value = false;
  if (outcome !== "conflict") return;
  await nextTick();
  papScheduleCard.value?.mergeSavedSchedule();
};

/**
 * The same rule for the criteria: a refused save keeps the form and its message (RV2-plan-editing-05).
 * Each press first previews the entered criteria and saves only when nothing conflicts; warnings wait
 * for a second press that acknowledges them. A refused save drops the preview, so the next press
 * asks again rather than saving over a conflict that appeared meanwhile. A preview answering after
 * the editor closed or the page moved to another deal is dropped: its save would write criteria
 * nobody is editing any more, or onto the other deal.
 */
const onSaveCriteria = async (
  body: EligibilityCriteriaRequest,
  acknowledged: boolean
) => {
  if (!acknowledged) {
    const dealId = store.dealId;
    const preview = await store.previewCriteria(body);
    if (preview === null || !editingCriteria.value || store.dealId !== dealId)
      return;
    criteriaPreview.value = {
      body,
      blocking: preview.blocking,
      warnings: preview.warnings,
    };
    if (preview.blocking.length > 0 || preview.warnings.length > 0) return;
  }
  if ((await store.saveCriteria(body)) === null) {
    criteriaPreview.value = null;
    return;
  }
  editingCriteria.value = false;
};

// The callers clear the action error first, except the metadata dialog's, which has always left a
// stale error where it was.
const openDialog = (dialog: DealDialog) => {
  activeDialog.value = { ...dialog, open: true };
};

const errorOn = (surface: ErrorSurface) => {
  const action = store.failedAction;
  const surfaces: readonly ErrorSurface[] =
    action === null ? ["header"] : ERROR_SURFACES[action];
  return surfaces.includes(surface) ? store.actionErrorMessage : null;
};

const onSliceAction = (action: SliceAction) => {
  if (action.kind === "escrow-pull") openPullModal(action.commitmentId);
  else openSliceModal(action.kind, action.sliceId);
};

const openSliceModal = (kind: SliceDialogKind, sliceId: number) => {
  store.clearActionError();
  commitmentWarnings.value = [];
  openDialog({ kind, sliceId });
  // Only the override needs the pool's strategy figures.
  const poolId = activeSlice.value?.pool_id ?? null;
  if (kind === "resize" && poolId !== null) void store.loadResizePool(poolId);
};

/** The dialog stays up until the backend agrees, so a refusal is read where it was caused. */
const onRemoveBacking = async () => {
  if (activeSliceId.value === null) return;
  const result = await store.deleteBacking(activeSliceId.value);
  if (result !== null) closeDialog("remove-backing");
};

/** A backed slice's volume is the commitment's and is resized there; open volume is the editor's. */
const onResize = async (volumeMwh: number, ignoreSalesStrategy: boolean) => {
  const slice = activeSlice.value;
  if (slice === null) return;
  const result = await store.resizeCommitment(
    slice.id,
    volumeMwh,
    ignoreSalesStrategy
  );
  if (result === null) return;
  // Growing a slice is an add-commitment from the same pool, so its feasibility warnings land on
  // the page the same way the add's do (Q-FIX3-01).
  commitmentWarnings.value = [
    ...result.resize.warnings,
    ...soldBeyondStrategyNotes(result.beyond_sales_strategy),
  ];
  closeDialog("resize");
};

const onMove = async (targetDeliveryId: number, volumeMwh: number | null) => {
  if (activeSliceId.value === null) return;
  if (
    (await store.moveSlice(
      activeSliceId.value,
      targetDeliveryId,
      volumeMwh
    )) !== null
  )
    closeDialog("move");
};

const onNarrow = async (width: PeriodWidth) => {
  if (activeSliceId.value === null) return;
  if ((await store.narrowSlice(activeSliceId.value, width)) !== null)
    closeDialog("narrow");
};

/** The attribution list is an admin read, so it is fetched only when the return form will show. */
const openEscrowModal = (commitmentId: number | null) => {
  store.clearActionError();
  openDialog({ kind: "escrow-return", commitmentId, results: null });
  if (escrowCanReturn.value) void store.loadEscrowAttributions();
};

const onReturnEscrow = async (
  selection: { attribution_id: number; count: number }[]
) => {
  if (escrowCommitmentGone.value) return;
  const opened = activeDialog.value;
  const result = await store.returnEscrow({ selection });
  // The results belong to the dialog that sent the return, not to one closed or reopened meanwhile.
  if (
    result !== null &&
    opened?.kind === "escrow-return" &&
    activeDialog.value === opened
  )
    activeDialog.value = { ...opened, results: result.items };
};

const openPullModal = (commitmentId: number) => {
  store.clearActionError();
  openDialog({ kind: "escrow-pull", commitmentId, results: null });
};

const onPullEscrow = async (body: EscrowPullRequest) => {
  if (pullCommitmentGone.value) return;
  const opened = activeDialog.value;
  const result = await store.pullEscrow(body);
  if (
    result !== null &&
    opened?.kind === "escrow-pull" &&
    activeDialog.value === opened
  )
    activeDialog.value = { ...opened, results: result.items };
};

const openProducerPricesModal = (deliveryId: number) => {
  store.clearActionError();
  openDialog({ kind: "producer-prices", deliveryId });
};

/**
 * The map is the delivery's whole set of prices: a producer left out invoices at its own price. A
 * stale save keeps the dialog open and merges its draft onto the reloaded delivery, as the
 * schedule card does.
 */
const onSaveProducerPrices = async (request: ProducerPricesRequest) => {
  if (pricedDeliveryId.value === null) return;
  const outcome = await store.saveProducerPrices(
    pricedDeliveryId.value,
    request
  );
  if (outcome === "saved") closeDialog("producer-prices");
  if (outcome !== "conflict") return;
  await nextTick();
  producerPricesModal.value?.mergeSavedPrices();
};

const openDeliveryNotesModal = (deliveryId: number) => {
  store.clearActionError();
  openDialog({ kind: "delivery-notes", deliveryId });
};

const onSaveDeliveryNotes = async (request: DeliveryNotesRequest) => {
  const delivery = notedDelivery.value;
  if (delivery === null) return;
  const result = await store.saveDeliveryNotes(delivery.id, request);
  if (result !== null) closeDialog("delivery-notes");
};

const openUnconfirmDialog = () => {
  store.clearActionError();
  openDialog({ kind: "unconfirm" });
};

const onUnconfirmDeal = async () => {
  const result = await store.unconfirm();
  if (result === null) return;
  closeDialog("unconfirm");
};

const openCancelDialog = () => {
  store.clearActionError();
  openDialog({ kind: "cancel" });
};

const onCancelDeal = async () => {
  const result = await store.cancel();
  if (result === null) return;
  closeDialog("cancel");
};

providePoolEditorAvailable(poolEditorAvailable);
</script>
